import React, { useEffect, useRef, useState } from 'react'
import api from '../../api/axios';
import { ChevronDown, DownloadIcon } from 'lucide-react';
import Popup from '../popup/Popup';
import QRDesigner from '../../pages/QREditor';

const Layout7Form = ({ data, onChange, errors }) => {
    const { video, brand, title, products, infoText } = data;

    const [campaigns, setCampaigns] = useState([]);
    const [openQrCodePopup, setOpenQrCodePopup] = useState(false);
    const fieldRefs = useRef({});

    useEffect(() => {
        if (!errors) return;

        const firstErrorKey = Object.keys(errors)[0];
        const el = fieldRefs.current[firstErrorKey];

        if (el) {
            el.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            // el.focus(); // optional (nice UX)
        }
    }, [errors])

    const handleChange = (section, field, value) => {
        // let cleanedValue = (value || '').replace(/%/g, "");

        let cleanedValue = value;

        // Only clean string values
        if (typeof value === "string") {
            cleanedValue = (value || '').replace(/%/g, "");
        }

        if (field === 'url' || field === 'logoUrl' || field === 'imageUrl' || field === 'qrCodeUrl') {
            cleanedValue = value
        }
        onChange('layout7', section, field, cleanedValue);
    };

    const getCampaigns = async () => {
        try {
            const response = await api.get('campaign/list');
            if (response && response.data && response.data.data) {
                setCampaigns(response.data.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getCampaigns();
    }, []);

    return (
        <div className="h-[calc(100vh-220px)]">
            {/* Canvas Settings */}
            <div className="form-section">

                <div className="form-group">
                    <label>Campaign Name</label>
                    <div className='relative'>
                        <select
                            ref={(el) => (fieldRefs.current["camp_name"] = el)}
                            value={data.camp_name}
                            onChange={(e) => handleChange(null, 'camp_name', e.target.value)}
                            className={`appearance-none ${errors?.camp_name ? "has-error" : ""} `}
                        >
                            <option value="">Select Campaign</option>
                            {campaigns.map((camp) => (
                                <option key={camp.camp_id} value={camp.camp_name}>
                                    {camp.camp_name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
                    </div>
                    {errors?.camp_name && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.camp_name}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Brand Name</label>

                    <input
                        type="text"
                        ref={(el) => (fieldRefs.current["brand_name"] = el)}
                        value={data.brand_name || ''}
                        onChange={(e) => handleChange(null, 'brand_name', e.target.value)}
                        className={errors?.brand_name ? "has-error" : ""}
                    />
                    {errors?.brand_name && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.brand_name}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Domain</label>
                    <input
                        type="text"
                        ref={(el) => (fieldRefs.current["domain"] = el)}
                        value={data.domain || ''}
                        onChange={(e) => handleChange(null, 'domain', e.target.value)}
                        className={errors?.domain ? "has-error" : ""}
                    />
                    {errors?.domain && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.domain}
                        </p>
                    )}
                </div>

                <div className="form-group">
                    <label>Creative Name</label>
                    <input
                        type="text"
                        ref={(el) => (fieldRefs.current["creative_name"] = el)}
                        value={data.creative_name || ''}
                        onChange={(e) => handleChange(null, 'creative_name', e.target.value)}
                        className={errors?.creative_name ? "has-error" : ""}
                    />
                    {errors?.creative_name && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.creative_name}
                        </p>
                    )}
                </div>
            </div>

            {/* Video Section */}
            <div className="form-section">
                <h3>Canvas and video settings</h3>

                <div className="form-group">
                    <label>Background Color</label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            value={data.canvas?.backgroundColor || '#6b1f23'}
                            onChange={(e) => handleChange('canvas', 'backgroundColor', e.target.value)}
                        />
                        <input
                            type="text"
                            value={data.canvas?.backgroundColor || '#6b1f23'}
                            onChange={(e) => handleChange('canvas', 'backgroundColor', e.target.value)}
                            placeholder="#6b1f23"
                            className="color-text-input"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Video URL (1920 x 1080)</label>
                    <input
                        type="text"
                        value={video.url}
                        onChange={(e) => handleChange('video', 'url', e.target.value)}
                        placeholder="Enter video URL"
                    />
                    <span className='text-xs text-blue-500'>Aspect ratio: (1920 x 1080)</span>
                </div>

                <div className="form-group">
                    <label>Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => handleChange(null, 'title', e.target.value)}
                        placeholder="Conquer Every Moment"
                        maxLength={30}
                    />
                </div>

                <div className="form-group">
                    <label>Text Color</label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            value={data.canvas?.textColor || '#121212'}
                            onChange={(e) => handleChange('canvas', 'textColor', e.target.value)} />
                        <input
                            type="text"
                            value={data.canvas?.textColor || '#121212'}
                            onChange={(e) => handleChange('canvas', 'textColor', e.target.value)}
                            placeholder="#121212"
                            className="color-text-input" />
                    </div>
                </div>

                <div className="form-group">
                    <label>Info Text</label>
                    <input
                        type="text"
                        value={infoText}
                        onChange={(e) => handleChange(null, 'infoText', e.target.value)}
                        placeholder="Scan for 10% offer"
                        maxLength={30}
                    />
                </div>

            </div>

            <div className="form-section">
                <h3>Products</h3>

                {[0, 1, 2, 3].map((index) => (
                    <div key={index} className="form-group">
                        <label>Product {index + 1} URL</label>

                        <input
                            type="text"
                            value={products[index] || ""}
                            placeholder="Enter product image URL"
                            onChange={(e) => {
                                const updatedProducts = [...products];
                                updatedProducts[index] = e.target.value;

                                handleChange(null, "products", updatedProducts);
                            }}
                        />
                        <p className="text-blue-600 text-xs">
                            Image must be at least 1080 × 1080 pixels.
                        </p>
                    </div>
                ))}
            </div>

            {/* Brand Section */}
            <div className="form-section">
                <h3>Brand</h3>

                <div className="form-group">
                    <label className='!flex justify-between cursor-pointer'
                        onClick={() => setOpenQrCodePopup(true)}>QR Code URL <span className='hover:text-blue-800 mr-2'
                        ><DownloadIcon className='h-4 w-4' /></span>
                    </label>
                    <input
                        type="text"
                        value={brand?.qrCodeUrl || ''}
                        onChange={(e) => handleChange('brand', 'qrCodeUrl', e.target.value)}
                        placeholder="Enter QR code URL"
                    />
                </div>

            </div>
            <Popup isOpen={openQrCodePopup} onClose={() => setOpenQrCodePopup(false)}>
                <QRDesigner />
            </Popup>
        </div>
    )
}

export default Layout7Form